/**
 * Theme derivation utilities
 * 
 * Functions to derive all theme tokens from the 5 core tokens + mode
 */

import type { ThemeMode, CoreThemeTokens, DerivedTheme, PlayerColorTokens } from './theme'

/**
 * Convert hex to HSL
 */
export function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const r = parseInt(hex.slice(1, 3), 16) / 255
  const g = parseInt(hex.slice(3, 5), 16) / 255
  const b = parseInt(hex.slice(5, 7), 16) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h = 0
  let s = 0
  const l = (max + min) / 2

  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break
      case g: h = ((b - r) / d + 2) / 6; break
      case b: h = ((r - g) / d + 4) / 6; break
    }
  }

  return { h: h * 360, s: s * 100, l: l * 100 }
}

/**
 * Convert HSL to hex
 */
export function hslToHex(h: number, s: number, l: number): string {
  s /= 100
  l /= 100

  const c = (1 - Math.abs(2 * l - 1)) * s
  const x = c * (1 - Math.abs((h / 60) % 2 - 1))
  const m = l - c / 2
  let r = 0, g = 0, b = 0

  if (0 <= h && h < 60) {
    r = c; g = x; b = 0
  } else if (60 <= h && h < 120) {
    r = x; g = c; b = 0
  } else if (120 <= h && h < 180) {
    r = 0; g = c; b = x
  } else if (180 <= h && h < 240) {
    r = 0; g = x; b = c
  } else if (240 <= h && h < 300) {
    r = x; g = 0; b = c
  } else if (300 <= h && h < 360) {
    r = c; g = 0; b = x
  }

  r = Math.round((r + m) * 255)
  g = Math.round((g + m) * 255)
  b = Math.round((b + m) * 255)

  const toHex = (n: number) => {
    const hex = n.toString(16)
    return hex.length === 1 ? '0' + hex : hex
  }

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}

/**
 * Adjust HSL lightness
 */
function adjustLightness(h: number, s: number, l: number, delta: number): string {
  return hslToHex(h, s, Math.max(0, Math.min(100, l + delta)))
}


/**
 * Derive text colors from textPrimary
 */
function deriveTextColors(textPrimary: string, mode: ThemeMode): {
  textSecondary: string
  textMuted: string
  textInverse: string
} {
  const hsl = hexToHsl(textPrimary)
  const isLight = mode === 'light'
  
  // textSecondary: 30-40% lighter/darker
  const secondaryDelta = isLight ? -35 : 35
  const textSecondary = adjustLightness(hsl.h, hsl.s, hsl.l, secondaryDelta)
  
  // textMuted: 50-60% lighter/darker
  const mutedDelta = isLight ? -55 : 55
  const textMuted = adjustLightness(hsl.h, hsl.s, hsl.l, mutedDelta)
  
  // textInverse: white in dark mode, black in light mode
  const textInverse = isLight ? '#000000' : '#ffffff'
  
  return { textSecondary, textMuted, textInverse }
}

/**
 * Derive accent variants from accent
 */
function deriveAccentVariants(accent: string, textInverse: string, mode: ThemeMode): {
  accentForeground: string
  accentBackground: string
} {
  const hsl = hexToHsl(accent)
  const isLight = mode === 'light'
  
  // accentForeground: use textInverse for contrast
  const accentForeground = textInverse
  
  // accentBackground: 10-20% lighter/darker shade
  const backgroundDelta = isLight ? -15 : 15
  const accentBackground = adjustLightness(hsl.h, hsl.s, hsl.l, backgroundDelta)
  
  return { accentForeground, accentBackground }
}

/**
 * Derive backgrounds from base backgrounds
 */
function deriveBackgrounds(_appBackground: string, surfaceBackground: string, mode: ThemeMode): {
  secondaryBackground: string
  mutedBackground: string
  subtleBackground: string
} {
  const surfaceHsl = hexToHsl(surfaceBackground)
  const isLight = mode === 'light'
  
  // secondaryBackground: 8-12% lighter/darker shade of surfaceBackground (slightly less than muted)
  const secondaryDelta = isLight ? -10 : 10
  const secondaryBackground = adjustLightness(surfaceHsl.h, surfaceHsl.s, surfaceHsl.l, secondaryDelta)
  
  // mutedBackground: 10-15% lighter/darker shade of appBackground or surfaceBackground
  const mutedDelta = isLight ? -12 : 12
  const mutedBackground = adjustLightness(surfaceHsl.h, surfaceHsl.s, surfaceHsl.l, mutedDelta)
  
  // subtleBackground: 5-10% lighter/darker shade of surfaceBackground
  const subtleDelta = isLight ? -7 : 7
  const subtleBackground = adjustLightness(surfaceHsl.h, surfaceHsl.s, surfaceHsl.l, subtleDelta)
  
  return { secondaryBackground, mutedBackground, subtleBackground }
}

/**
 * Derive borders from textPrimary
 */
function deriveBorders(textPrimary: string, accent: string, mode: ThemeMode): {
  border: string
  divider: string
  focusRing: string
} {
  const textHsl = hexToHsl(textPrimary)
  const isLight = mode === 'light'
  
  // border: 20-30% lighter/darker shade of textPrimary with reduced opacity effect
  const borderDelta = isLight ? -25 : 25
  const border = adjustLightness(textHsl.h, textHsl.s, textHsl.l, borderDelta)
  
  // divider: lighter shade of border
  const borderHsl = hexToHsl(border)
  const dividerDelta = isLight ? -5 : 5
  const divider = adjustLightness(borderHsl.h, borderHsl.s, borderHsl.l, dividerDelta)
  
  // focusRing: uses accent
  const focusRing = accent
  
  return { border, divider, focusRing }
}

/**
 * Derive danger variants from danger color (renamed from error)
 */
function deriveDangerVariants(danger: string, textInverse: string, mode: ThemeMode): {
  dangerBackground: string
  dangerBorder: string
  dangerForeground: string
} {
  const hsl = hexToHsl(danger)
  const isLight = mode === 'light'
  
  // dangerBackground: Light tint - increase lightness 40-50%, decrease saturation 30-40%
  const backgroundLightness = Math.min(100, hsl.l + (isLight ? 40 : -40))
  const backgroundSaturation = Math.max(0, hsl.s - 35)
  const dangerBackground = hslToHex(hsl.h, backgroundSaturation, backgroundLightness)
  
  // dangerBorder: Medium shade - decrease lightness 10-20%
  const borderLightness = Math.max(0, Math.min(100, hsl.l - (isLight ? 15 : -15)))
  const dangerBorder = hslToHex(hsl.h, hsl.s, borderLightness)
  
  // dangerForeground: Dark shade - decrease lightness 30-40% or use textInverse if very light
  if (hsl.l > 70) {
    // Danger is very light, use textInverse
    const dangerForeground = textInverse
    return { dangerBackground, dangerBorder, dangerForeground }
  } else {
    const foregroundLightness = Math.max(0, hsl.l - (isLight ? 35 : -35))
    const dangerForeground = hslToHex(hsl.h, hsl.s, foregroundLightness)
    return { dangerBackground, dangerBorder, dangerForeground }
  }
}

/**
 * Derive core tokens from accent only
 */
export function deriveCoreTokensFromAccent(_accent: string, mode: ThemeMode): Omit<CoreThemeTokens, 'accent'> {
  const isLight = mode === 'light'
  
  // appBackground: black in dark mode, white in light mode
  const appBackground = isLight ? '#ffffff' : '#000000'
  
  // surfaceBackground: white in light mode, slightly lighter than appBackground in dark mode
  const surfaceBackground = isLight ? '#ffffff' : '#0a0a0a'
  
  // textPrimary: black in light mode, white in dark mode
  const textPrimary = isLight ? '#1a1a1a' : '#fafafa'
  
  // danger: fixed semantic red (renamed from error)
  const danger = isLight ? '#ef4444' : '#dc2626'
  
  return { appBackground, surfaceBackground, textPrimary, danger }
}

/**
 * Derive all theme tokens from core tokens
 */
export function deriveTheme(
  core: CoreThemeTokens,
  playerColors: PlayerColorTokens[],
  mode: ThemeMode,
  providedByGame: DerivedTheme['providedByGame'],
  neutral: PlayerColorTokens | null = null
): DerivedTheme {
  // Derive text colors
  const textColors = deriveTextColors(core.textPrimary, mode)
  
  // Derive accent variants
  const accentVariants = deriveAccentVariants(core.accent, textColors.textInverse, mode)
  
  // Derive backgrounds
  const backgrounds = deriveBackgrounds(core.appBackground, core.surfaceBackground, mode)
  
  // Derive borders
  const borders = deriveBorders(core.textPrimary, core.accent, mode)
  
  // Derive danger variants (renamed from error)
  const dangerVariants = deriveDangerVariants(core.danger, textColors.textInverse, mode)
  
  // Overlays & shadows
  const overlayBackground = mode === 'light' ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.5)'
  const shadowColor = 'rgba(0, 0, 0, 0.1)'
  
  // Selection
  const selectionBackground = `${core.accent}33` // accent with 20% opacity
  const selectionText = textColors.textInverse
  
  return {
    core,
    playerColors,
    neutral,
    providedByGame,
    derived: {
      ...textColors,
      ...accentVariants,
      ...backgrounds,
      ...borders,
      ...dangerVariants,
      overlayBackground,
      shadowColor,
      selectionBackground,
      selectionText,
    },
  }
}

